import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import tWithDefault from "parog-web/utils/t-with-default";
import type HemodialysisSessionDeclaredJournalEvent from "./hemodialysis-session-declared-journal-event";

//---- Automatically generated, don't update this part ----

export default class HemodialysisMeasuredJournalEvent extends JournalEvent {
  static override type = "hemodialysis-measured-journal-event";

  /**
   * Dialysis moment, start/middle/end
   */
  @attr("string") declare moment: string;

  /**
   * Real weight start of the session
   */
  @attr("number") declare startRealWeight: number;

  /**
   * Dry weight start of the session
   */
  @attr("number") declare startDryWeight: number;

  /**
   * Arterial systolic pressure start of the session
   */
  @attr("number") declare startArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure start of the session
   */
  @attr("number") declare startArterialDiastolicPressure: number;

  /**
   * Heart rate start of the session
   */
  @attr("number") declare startHeartRate: number;

  /**
   * Total ultrafiltration to be donestart of the session
   */
  @attr("number") declare startUltrafiltrationTotal: number;

  /**
   * Arterial systolic pressure middle of the session
   */
  @attr("number") declare middleArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure middle of the session
   */
  @attr("number") declare middleArterialDiastolicPressure: number;

  /**
   * Heart rate middle of the session
   */
  @attr("number") declare middleHeartRate: number;

  /**
   * Ultrafiltration middle of the session
   */
  @attr("number") declare middleUltrafiltration: number;

  /**
   * Machine arterial pressure middle of the session
   */
  @attr("number") declare middleMachineArterialPressure: number;

  /**
   * Machine venous pressure middle of the session
   */
  @attr("number") declare middleMachineVenousPressure: number;

  /**
   * Output weight end of the session
   */
  @attr("number") declare endOutputWeight: number;

  /**
   * Arterial systolic pressure end of the session
   */
  @attr("number") declare endArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure end of the session
   */
  @attr("number") declare endArterialDiastolicPressure: number;

  /**
   * Heart rate end of the session
   */
  @attr("number") declare endHeartRate: number;

  /**
   * Venous compression duration end of the session
   */
  @attr("number") declare endVenousCompressionTime: number;

  /**
   * Arterial compression duration end of the session
   */
  @attr("number") declare endArterialCompressionTime: number;

  @belongsTo("hemodialysis-session-declared-journal-event", {
    async: false,
    inverse: "hemodialysisMeasuredJournalEvents"
  })
  declare hemodialysisSessionDeclaredJournalEvent: HemodialysisSessionDeclaredJournalEvent;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.hemodialysis-measured-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      startRealWeight: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-real-weight"
        )
      }),
      startDryWeight: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-dry-weight"
        )
      }),
      startArterialSystolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-arterial-systolic-pressure"
        )
      }),
      startArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-arterial-diastolic-pressure"
        )
      }),
      startHeartRate: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-heart-rate"
        )
      }),
      startUltrafiltrationTotal: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.start-ultrafiltration-total"
        )
      }),
      middleArterialSystolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-arterial-systolic-pressure"
        )
      }),
      middleArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-arterial-diastolic-pressure"
        )
      }),
      middleHeartRate: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-heart-rate"
        )
      }),
      middleUltrafiltration: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-ultrafiltration"
        )
      }),
      middleMachineArterialPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-machine-arterial-pressure"
        )
      }),
      middleMachineVenousPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.middle-machine-venous-pressure"
        )
      }),
      endOutputWeight: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-output-weight"
        )
      }),
      endArterialSystolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-arterial-systolic-pressure"
        )
      }),
      endArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-arterial-diastolic-pressure"
        )
      }),
      endHeartRate: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-heart-rate"
        )
      }),
      endVenousCompressionTime: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-venous-compression-time"
        )
      }),
      endArterialCompressionTime: validateNumber({
        allowBlank: true,
        gte: 0,
        description: intl.t(
          "validations.hemodialysis-measured-journal-event.end-arterial-compression-time"
        )
      })
    };
  }

  //---- End of generated code ----
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "hemodialysis-measured-journal-event": HemodialysisMeasuredJournalEvent;
  }
}
